import {TooltipProps, Tooltip} from '@mantine/core'
import {useDebouncedValue, useClickOutside} from '@mantine/hooks'
import {isValidElement, useCallback, useState} from 'react'
import {values} from 'remeda'
import {DarkSchemedChildren} from './ColorSchemedChildren'
import React from 'react'
import {useLatest} from './useLatest'

export const useTip = () => {
  const [openedState, setOpenedState] = useState(false)
  const hovered = {
    target: useState(false),
    tip: useState(false)
  }
  const isHovered = values(hovered).some(h => h[0])
  const [isHoveredDebounce] = useDebouncedValue(
    values(hovered).some(h => h[0]),
    100
  )
  const opened = openedState || isHovered || isHoveredDebounce
  const hoverProps = (key: keyof typeof hovered) => ({
    onMouseEnter: () => {
      hovered[key][1](true)
    },
    onMouseLeave: () => {
      hovered[key][1](false)
    }
  })

  const latest = useLatest({setOpenedState, hovered})
  const setOpened = useCallback((opened: boolean) => {
    latest.current.setOpenedState(opened)
    if (!opened) values(latest.current.hovered).forEach(h => h[1](false))
  }, []) // stable reference, while getting latest values

  return {setOpened, opened, hoverProps, openedState}
}
export type UseTip = ReturnType<typeof useTip>
export type TipProps = Omit<TooltipProps, 'opened'> & {
  store?: UseTip
  closeOnClickOutside?: boolean
}

/** Tooltip with a controlled open state with sticky open behavior */
export const Tip = ({children, label, store, closeOnClickOutside = true, ...props}: TipProps) => {
  const internalStore = useTip()
  const {opened, setOpened, hoverProps, openedState} = store ?? internalStore
  const clickOutsideRef = useClickOutside(() => setOpened(false))
  const wrappedChildren = isValidElement(children) ? children : <div>{children}</div>
  return (
    <Tooltip
      opened={opened}
      withArrow
      arrowSize={4}
      arrowOffset={10}
      multiline
      {...hoverProps('tip')}
      onClick={() => setOpened(!openedState)}
      label={
        label ?
          <div
            {...hoverProps('target')}
            ref={closeOnClickOutside ? clickOutsideRef : undefined}
            className='pointer-events-auto'>
            <DarkSchemedChildren>{label}</DarkSchemedChildren>
          </div>
        : undefined
      }
      {...props}>
      {wrappedChildren}
    </Tooltip>
  )
}
